import { Breakpoint, ThemeColor } from '@/enums'
import { snakeToCamelCase, themeBreakpointUp, themeColor } from '@/utils'
import styled from 'styled-components'
import { BestOfferBadge } from '../../styles'
import { useTranslation } from 'react-i18next'
import { AdaptiveTooltip, Switch } from '@surein/ui'
import {
  Building,
  Car,
  FileText,
  Info,
  Siren,
  UserCircle,
  Users,
} from 'lucide-react'
import { theme } from '@/theme'
import { SupportedCustomizationOptionsKeys } from '@/types/recommendation'
import { useCallback, useEffect, useState } from 'react'
import { AdvisorTip } from '@/components/molecules/AdvisorTip/AdvisorTip'
import { useUpdateRecommendationOptions } from '@/services'
import { useSnackbar } from '@/providers/SnackbarProvider'
import { useSegment } from '@/modules/analytics'

enum ToggleState {
  RECOMMENDED_OPTION_DISABLED = 'recommended-option-disabled',
  NON_RECOMMENDED_OPTION_ENABLED = 'non-recommended-option-enabled',
  NORMAL = 'normal',
}

const BorderColors = {
  [ToggleState.RECOMMENDED_OPTION_DISABLED]: themeColor(ThemeColor.warning1),
  [ToggleState.NON_RECOMMENDED_OPTION_ENABLED]: themeColor(ThemeColor.success1),
  [ToggleState.NORMAL]: themeColor(ThemeColor.b30),
}

const BackgroundColors = {
  [ToggleState.RECOMMENDED_OPTION_DISABLED]: themeColor(ThemeColor.warning4),
  [ToggleState.NON_RECOMMENDED_OPTION_ENABLED]: themeColor(ThemeColor.success4),
  [ToggleState.NORMAL]: themeColor(ThemeColor.glass100),
}

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ToggleContainer = styled.div<{ state: ToggleState }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-height: 89px;
  border-radius: 8px;
  border: 1px solid ${({ state }) => BorderColors[state]};
  background: ${({ state }) => BackgroundColors[state]};
  padding: 16px;
  gap: 8px;

  ${themeBreakpointUp(Breakpoint.md)} {
    flex-direction: row;
  }
`
const ToggleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;

  ${themeBreakpointUp(Breakpoint.md)} {
    width: initial;
    justify-content: space-between;
  }
`

const ToggleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;

  ${themeBreakpointUp(Breakpoint.md)} {
    width: initial;
    justify-content: space-between;
  }
`

const StyledBestOfferBadge = styled(BestOfferBadge)`
  width: fit-content;
  background: ${themeColor(ThemeColor.info3)};
`

const InfoIconWrapper = styled.div`
  cursor: pointer;
`

const iconsMap = {
  [SupportedCustomizationOptionsKeys.TRAFFIC_COVERAGE]: (
    <Car color={theme.color.info1} />
  ),
  [SupportedCustomizationOptionsKeys.EMPLOYER_LABOR_LAW_PROTECTION_AREA]: (
    <Users color={theme.color.info1} />
  ),
  [SupportedCustomizationOptionsKeys.REAL_ESTATE_COVERAGE]: (
    <Building color={theme.color.info1} />
  ),
  [SupportedCustomizationOptionsKeys.SPECIAL_CRIMINAL_LEGAL_PROTECTION_INSURANCE]: (
    <Siren color={theme.color.info1} />
  ),
  [SupportedCustomizationOptionsKeys.PRIVATE_GENERAL_LEGAL_PROTECTION]: (
    <UserCircle color={theme.color.info1} />
  ),
  [SupportedCustomizationOptionsKeys.COMPANY_CONTRACT_LEGAL_PROTECTION]: (
    <FileText color={theme.color.info1} />
  ),
}

interface Props {
  optionKey: SupportedCustomizationOptionsKeys
  isRecommended: boolean
  recommendationId: string
  initialValue: boolean
}

export const RecommendationToggle = ({
  optionKey,
  isRecommended,
  recommendationId,
  initialValue,
}: Props) => {
  const { t } = useTranslation()
  const analytics = useSegment()
  const translationOptionKey = snakeToCamelCase(optionKey)
  const [isChecked, setIsChecked] = useState(isRecommended)
  const snackbar = useSnackbar()
  const updateRecommendationOptionsMutation = useUpdateRecommendationOptions(
    recommendationId,
  )
  const isAdvisorTipVisible = isChecked !== isRecommended
  const toggleState = !isAdvisorTipVisible
    ? ToggleState.NORMAL
    : !isChecked
    ? ToggleState.RECOMMENDED_OPTION_DISABLED
    : ToggleState.NON_RECOMMENDED_OPTION_ENABLED
  const translationStateKey =
    toggleState === ToggleState.RECOMMENDED_OPTION_DISABLED
      ? 'recommendedOptionDisabledWarning'
      : toggleState === ToggleState.NON_RECOMMENDED_OPTION_ENABLED
      ? 'nonRecommendedOptionEnabledWarning'
      : ''

  const handleCheckedChange = useCallback(
    async (newValue: boolean) => {
      const oldValue = isChecked
      setIsChecked(newValue)
      try {
        await updateRecommendationOptionsMutation.mutateAsync({
          recommendationId,
          options: [
            {
              name: optionKey,
              value: newValue,
            },
          ],
        })

        if (newValue === isRecommended) {
          await analytics.track('recommendation_recommended_state', {
            optionKey,
          })
        }

        await analytics.track('recommendation_options_change', {
          optionKey,
          value: newValue,
          isRecommended,
        })
      } catch {
        setIsChecked(oldValue)
        snackbar.error(t('snackbars.error.generalMsg'))
      }
    },
    [recommendationId],
  )

  useEffect(() => {
    setIsChecked(initialValue)
  }, [initialValue])

  return (
    <ComponentWrapper>
      <ToggleContainer state={toggleState}>
        <ToggleTitle>
          <div>{iconsMap[optionKey]}</div>
          <div>
            {' '}
            {t(`recommendations.customization.${translationOptionKey}.title`)}
          </div>
        </ToggleTitle>
        <ToggleSection>
          {isRecommended ? (
            <StyledBestOfferBadge>
              {t('recommendations.customization.recommendedBadgeLabel')}
            </StyledBestOfferBadge>
          ) : null}

          <AdaptiveTooltip
            content={t(
              `recommendations.customization.${translationOptionKey}.sectionInfoMessage`,
            )}
          >
            <InfoIconWrapper>
              <Info height={24} />
            </InfoIconWrapper>
          </AdaptiveTooltip>
          <Switch checked={isChecked} onCheckedChange={handleCheckedChange} />
        </ToggleSection>
      </ToggleContainer>
      {isAdvisorTipVisible ? (
        <AdvisorTip
          title={t(`recommendations.customization.${translationStateKey}`)}
          message={t(
            `recommendations.customization.${translationOptionKey}.advisorTipMessage.${translationStateKey}`,
          )}
        />
      ) : null}
    </ComponentWrapper>
  )
}
